import './DateControl.scss';

import { format, isValid, parse } from 'date-fns';
import React, { useCallback, useReducer, useRef, useState } from 'react';

import type { FilterConfig } from '../../../../../apis/graphql/types';
import { DateOperator, FilterOperator, RelativeDirection, RelativeUnit } from '../../../../../apis/graphql/types';
import type { DropdownOption } from '../../../../../common/Dropdown';
import Dropdown from '../../../../../common/Dropdown';
import bem from '../../../../../utils/bem';
import useScrollAwareMenuPlacement from '../../hooks/useScrollAwareMenuPlacement';
import type { CalendarDate } from './FilterDatePicker';
import { FilterDatePicker } from './FilterDatePicker';
import type { PartialRelativeValue } from './RelativeDateControl';
import RelativeDateControl from './RelativeDateControl';
import {
  computeRelativeRange,
  formatRangeBound,
  formatRelativeRangeDisplay,
  resolveConditionalBoundary,
  todayLocalIso,
} from './relativeDateRange';
import type {
  ComparisonDateOperator,
  DateFilter,
  RangeDateFilter,
  RelativeDateFilter,
  SingleDateFilter,
  SingleDateOperator,
} from './types';
import { isComparisonDateOperator, isConditionalDateFilter, isRelativeDateFilter, isSingleDateOperator } from './types';

// Re-exported for existing importers now that the union and its guards live in ./types.
export type { DateFilter, RangeDateFilter, RelativeDateFilter, SingleDateFilter, SingleDateOperator };

export type DateControlProps = {
  config: FilterConfig;
  value: DateFilter;
  onChange: (id: string, value: DateFilter) => void;
};

const buildDefaultRelativeFilter = (): RelativeDateFilter => ({
  operator: DateOperator.IsRelativeToToday,
  direction: RelativeDirection.Next,
  value: 7,
  unit: RelativeUnit.Day,
  referenceDate: todayLocalIso(),
});

// Fully unset: no direction, no unit, blank amount. Never pre-filled — a pre-filled row would
// look active when it is inert. Always carries a concrete referenceDate (rather than widening
// that field too) so RelativeDateControl's own value/initialReferenceDate fallback chain is
// never exercised for this caller.
const buildUnsetConditionalDraft = (): PartialRelativeValue => ({ value: null, referenceDate: todayLocalIso() });

// THIS needs only a unit; PAST/NEXT need amount and unit too. Deliberately independent of
// computeRelativeRange — no date parsing, no referenceDate dependency, trivial to unit test.
const isCompleteRelativeTriple = (
  direction: RelativeDirection | undefined,
  amount: number | null,
  unit: RelativeUnit | undefined
): boolean => {
  if (!direction || !unit) return false;
  return direction === RelativeDirection.This || amount != null;
};

const DATE_FORMAT = 'yyyy-MM-dd';

const OPERATOR_LABELS: Record<string, string> = {
  [DateOperator.Is]: 'Is',
  [DateOperator.IsBefore]: 'Is before',
  [DateOperator.IsAfter]: 'Is after',
  [DateOperator.IsOnOrBefore]: 'Is on or before',
  [DateOperator.IsOnOrAfter]: 'Is on or after',
  [DateOperator.IsBetween]: 'Is Between',
  [DateOperator.IsRelativeToToday]: 'Is Relative to Today',
};

// The filter config advertises operators using the FilterOperator enum, whose range
// member is BETWEEN. The lineItemEndDate value is a DateOperatorFilter, whose enum is
// DateOperator and uses IS_BETWEEN. Single-date operators share identical string values
// across both enums, so only the range operator needs translating to a valid DateOperator.
const toDateOperator = (op: string): DateOperator =>
  op === FilterOperator.Between ? DateOperator.IsBetween : (op as DateOperator);

const parseDateString = (dateStr: string | null | undefined): Date | undefined => {
  if (!dateStr) return undefined;
  const parsed = parse(dateStr, DATE_FORMAT, new Date());
  return isValid(parsed) ? parsed : undefined;
};

const [block] = bem('date-control');

const DateControl = ({ config, value, onChange }: DateControlProps): JSX.Element => {
  const { menuPlacement, maxMenuHeight, placementRef, onOpenMenu } = useScrollAwareMenuPlacement();
  const draftRef = useRef<DateFilter>(value);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  // KATANA-1752: the conditional-boundary triple, held locally as a *partial* draft — DateControl
  // only ever hands `onChange` a real ConditionalDateFilter once it's complete (see
  // isCompleteRelativeTriple below); while incomplete it commits `{ operator, date: null }`
  // instead, keeping ConditionalDateFilter's non-null contract intact. Seeded from an incoming
  // ConditionalDateFilter on hydration; otherwise fully unset.
  const [conditionalDraft, setConditionalDraft] = useState<PartialRelativeValue>(() =>
    isConditionalDateFilter(value)
      ? { direction: value.direction, value: value.value, unit: value.unit, referenceDate: value.referenceDate }
      : buildUnsetConditionalDraft()
  );

  const operatorOptions: DropdownOption[] = (config.operators ?? []).map((op) => {
    const operatorValue = toDateOperator(op);
    return {
      label: OPERATOR_LABELS[operatorValue] ?? operatorValue,
      value: operatorValue,
    };
  });

  const selectedOperatorOption = operatorOptions.find((o) => o.value === value.operator) ?? null;

  const handleOperatorChange = useCallback(
    (option: DropdownOption | null): void => {
      if (!option) return;
      const nextOp = option.value;

      // Is, Is Between and Is Relative to Today never carry a conditional boundary — their value
      // shapes have no room for direction/value/unit, and the ticket scopes conditional entry to
      // only the four comparison operators. Reset before building `next` below so a stale
      // conditional never resurfaces after a detour through one of these three.
      if (!isComparisonDateOperator(nextOp)) {
        setConditionalDraft(buildUnsetConditionalDraft());
      }

      // Each branch builds the next value explicitly: spreading the previous one would strand
      // fields from the old operator's shape in saved filters and shareable links.
      if (isSingleDateOperator(nextOp)) {
        // Carry a *complete* conditional across the four comparison operators — the triple means
        // the same thing regardless of which of the four is comparing against it.
        if (
          isComparisonDateOperator(nextOp) &&
          isCompleteRelativeTriple(conditionalDraft.direction, conditionalDraft.value, conditionalDraft.unit)
        ) {
          const next: DateFilter = {
            operator: nextOp,
            direction: conditionalDraft.direction as RelativeDirection,
            value: conditionalDraft.value,
            unit: conditionalDraft.unit as RelativeUnit,
            referenceDate: conditionalDraft.referenceDate,
          };
          draftRef.current = next;
          onChange(config.id, next);
          return;
        }

        // KATANA-1752 fix: the previous value may be a ConditionalDateFilter, which has no `.date`
        // field — reading it via the old unconditional cast landed `undefined`, not `null`, despite
        // the `string | null` type. isConditionalDateFilter guards that explicitly.
        const prevDate =
          isSingleDateOperator(value.operator) && !isConditionalDateFilter(value)
            ? (value as SingleDateFilter).date
            : null;
        const next: DateFilter = { operator: nextOp, date: prevDate };
        draftRef.current = next;
        onChange(config.id, next);
      } else if (nextOp === DateOperator.IsBetween) {
        const next: DateFilter = { operator: DateOperator.IsBetween, startDate: null, endDate: null };
        draftRef.current = next;
        onChange(config.id, next);
      } else if (nextOp === DateOperator.IsRelativeToToday) {
        // Always a complete default — the backend validator rejects a partial relative filter.
        // Unchanged from before this ticket: always resets to NEXT 7 DAY regardless of what the
        // previous operator/value was, including a conditional boundary. Grilled and confirmed —
        // matches the existing regression-locked behavior rather than carrying the triple over.
        const next: DateFilter = buildDefaultRelativeFilter();
        draftRef.current = next;
        onChange(config.id, next);
      }
    },
    [config.id, onChange, value, conditionalDraft]
  );

  const handleRelativeChange = useCallback(
    (next: RelativeDateFilter): void => {
      // Relative changes commit immediately; unlike calendar edits they aren't staged until Apply.
      draftRef.current = next;
      onChange(config.id, next);
    },
    [config.id, onChange]
  );

  const handleRangeDateChange = useCallback((dates: CalendarDate | CalendarDate[]): void => {
    if (Array.isArray(dates)) {
      draftRef.current = {
        operator: DateOperator.IsBetween,
        startDate: dates[0] && isValid(dates[0]) ? format(dates[0], DATE_FORMAT) : null,
        endDate: dates[1] && isValid(dates[1]) ? format(dates[1], DATE_FORMAT) : null,
      };
      forceUpdate();
    }
  }, []);

  const handleSingleDateChange = useCallback(
    (d: CalendarDate | CalendarDate[]): void => {
      if (!Array.isArray(d)) {
        // First touch, date-picking direction: picking a calendar date clears the conditional
        // boundary, even if it was only partially filled in. The two authoring modes are mutually
        // exclusive from the first interaction, not just once one of them is complete.
        setConditionalDraft(buildUnsetConditionalDraft());
        draftRef.current = {
          operator: (value as SingleDateFilter).operator,
          date: d && isValid(d) ? format(d, DATE_FORMAT) : null,
        };
        forceUpdate();
      }
    },
    [value]
  );

  const handleApply = useCallback((): void => {
    onChange(config.id, draftRef.current);
  }, [config.id, onChange]);

  // KATANA-1752: the other first-touch direction — any interaction with the conditional row
  // clears an already-picked calendar date, even while the triple is still incomplete. Passed as
  // RelativeDateControl's `onChange` for the conditional caller only; see the KATANA-1752 note on
  // that component for why its declared `RelativeDateFilter` parameter type doesn't reflect the
  // partial shape this callback actually receives at runtime.
  const handleConditionalChange = useCallback(
    (patch: RelativeDateFilter): void => {
      const nextDraft = (patch as unknown) as PartialRelativeValue;
      setConditionalDraft(nextDraft);

      if (isCompleteRelativeTriple(nextDraft.direction, nextDraft.value, nextDraft.unit)) {
        const next: DateFilter = {
          operator: (value as SingleDateFilter).operator as ComparisonDateOperator,
          direction: nextDraft.direction as RelativeDirection,
          value: nextDraft.value,
          unit: nextDraft.unit as RelativeUnit,
          referenceDate: nextDraft.referenceDate,
        };
        draftRef.current = next;
        onChange(config.id, next);
      } else {
        // Incomplete: commit { operator, date: null } — the real ConditionalDateFilter only ships
        // once the triple resolves to an actual boundary.
        const next: DateFilter = { operator: (value as SingleDateFilter).operator, date: null };
        draftRef.current = next;
        onChange(config.id, next);
      }
    },
    [config.id, onChange, value]
  );

  const handleClear = useCallback((): void => {
    setConditionalDraft(buildUnsetConditionalDraft());
    if (isRelativeDateFilter(value)) {
      // Reset to the default shape, not null — a null direction/unit is invalid per the validator.
      const cleared: DateFilter = buildDefaultRelativeFilter();
      draftRef.current = cleared;
      onChange(config.id, cleared);
      return;
    }
    if (value.operator === DateOperator.IsBetween) {
      const cleared: DateFilter = { operator: DateOperator.IsBetween, startDate: null, endDate: null };
      draftRef.current = cleared;
      onChange(config.id, cleared);
    } else {
      const cleared: DateFilter = { operator: (value as SingleDateFilter).operator, date: null };
      draftRef.current = cleared;
      onChange(config.id, cleared);
    }
  }, [config.id, onChange, value]);

  const renderInput = (): JSX.Element | null => {
    const draft = draftRef.current;

    if (isRelativeDateFilter(draft)) {
      return (
        <FilterDatePicker
          relativeMode
          relativeRange={computeRelativeRange(draft)}
          displayValue={formatRelativeRangeDisplay(draft)}
          placeholderText="Select relative range"
          calendarHeaderContent={<RelativeDateControl value={draft} onChange={handleRelativeChange} />}
          onApply={handleApply}
          onClear={handleClear}
        />
      );
    }

    if (draft.operator === DateOperator.IsBetween) {
      const startDate = parseDateString(draft.startDate);
      const endDate = parseDateString(draft.endDate);

      return (
        <FilterDatePicker
          selectsRange
          dateRange={[startDate ?? null, endDate ?? null]}
          placeholderText="Select date range"
          onChange={handleRangeDateChange}
          onApply={handleApply}
          onClear={handleClear}
        />
      );
    }

    // Single-date operators
    const selectedDate = parseDateString((draft as SingleDateFilter).date);

    const showConditionalControl = isComparisonDateOperator(draft.operator);

    // KATANA-1752: isConditionalDateFilter is only ever true for a *complete*
    // triple — this file never commits an incomplete one as a ConditionalDateFilter — so this
    // can't render a half-finished boundary. Without this, hydrating a ConditionalDateFilter
    // (e.g. from a shareable link) would render the single-date input blank while the filter is
    // still active and being sent to the backend.
    // KATANA-1753: show the resolved boundary date rather than the expression text.
    const conditionalBoundary = isConditionalDateFilter(draft)
      ? resolveConditionalBoundary(draft.operator, draft)
      : null;

    const displayValue = conditionalBoundary ? formatRangeBound(conditionalBoundary) : undefined;

    return (
      <FilterDatePicker
        date={conditionalBoundary ?? selectedDate}
        displayValue={displayValue}
        placeholderText="Select date"
        onChange={handleSingleDateChange}
        calendarHeaderContent={
          showConditionalControl ? (
            <RelativeDateControl
              value={conditionalDraft}
              onChange={handleConditionalChange}
              dataTestId="conditional-boundary-control"
            />
          ) : undefined
        }
        onApply={handleApply}
        onClear={handleClear}
      />
    );
  };

  return (
    <div className={block()} data-testid="date-control" ref={placementRef} onMouseDown={onOpenMenu}>
      <div className={`${block()}__operator-dropdown`}>
        <Dropdown
          options={operatorOptions}
          value={selectedOperatorOption}
          onChange={handleOperatorChange}
          emptyDisplayText="Select operator"
          dataTestId="date-control-operator-dropdown"
          menuPlacement={menuPlacement}
          maxMenuHeight={maxMenuHeight}
        />
      </div>
      <div className={`${block()}__input`}>{renderInput()}</div>
    </div>
  );
};

export default DateControl;
